import { BrandKitColorSettings } from "../models/BrandKit";

/** Hard cap on the number of colors in a brand kit palette. */
export const MAX_BRAND_COLORS = 8;

export function normalizeHexColor(
  color: string | null | undefined
): string | null {
  if (!color) return null;

  const trimmed = color.trim();
  const shortHex = trimmed.match(/^#([0-9a-fA-F]{3})$/);
  if (shortHex) {
    return `#${shortHex[1]
      .split("")
      .map((char) => `${char}${char}`)
      .join("")
      .toUpperCase()}`;
  }

  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) {
    return trimmed.toUpperCase();
  }

  return null;
}

/**
 * Parses what a person typed into a hex color field. Accepts `#rgb` and
 * `#rrggbb`, with or without the leading `#`, ignoring surrounding
 * whitespace. Returns normalized `#RRGGBB`, or null while the text is not
 * (yet) a complete color.
 */
export function parseHexColorInput(input: string | null | undefined): string | null {
  if (!input) return null;
  const trimmed = input.trim();
  return normalizeHexColor(trimmed.startsWith("#") ? trimmed : `#${trimmed}`);
}

/**
 * Flattens BrandKitColorSettings into the ordered palette list:
 * primary, secondary, tertiary, then additionalColors.
 *
 * `dedupe: false` preserves duplicate entries so editing UIs keep
 * stable positions; consumers should use the default (deduped) form.
 */
export function getBrandColorList(
  colors: BrandKitColorSettings | null | undefined,
  options: { dedupe?: boolean } = {}
): string[] {
  if (!colors) return [];
  const { dedupe = true } = options;

  const raw = [
    colors.primaryColor,
    colors.secondaryColor,
    colors.tertiaryColor,
    ...(colors.additionalColors ?? []),
  ];

  const seen = new Set<string>();
  const list: string[] = [];
  for (const candidate of raw) {
    const hex = normalizeHexColor(candidate);
    if (!hex) continue;
    if (dedupe) {
      const key = hex.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
    }
    list.push(hex);
    if (list.length >= MAX_BRAND_COLORS) break;
  }
  return list;
}

/**
 * Maps an ordered palette list back onto the stored shape: the first three
 * positions fill the legacy role fields, the rest land in additionalColors.
 */
export function brandColorListToSettings(
  list: string[]
): BrandKitColorSettings {
  const capped = list.slice(0, MAX_BRAND_COLORS);
  const [primary, secondary, tertiary, ...rest] = capped;
  return {
    primaryColor: primary ?? null,
    secondaryColor: secondary ?? null,
    tertiaryColor: tertiary ?? null,
    additionalColors: rest,
  };
}

/** Position labels: the first three carry legacy template roles. */
export function brandColorPositionLabel(index: number): string {
  if (index === 0) return "Primary";
  if (index === 1) return "Secondary";
  if (index === 2) return "Tertiary";
  return `Color ${index + 1}`;
}

/**
 * Merges two palettes preserving order and dropping duplicates —
 * used when website extraction results land on top of existing colors.
 */
export function mergeBrandColorLists(
  leading: string[],
  trailing: string[]
): string[] {
  const seen = new Set<string>();
  const merged: string[] = [];
  for (const color of [...leading, ...trailing]) {
    const hex = normalizeHexColor(color);
    if (!hex) continue;
    const key = hex.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(hex);
    if (merged.length >= MAX_BRAND_COLORS) break;
  }
  return merged;
}
